<template>
  <div>
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      :disabled="loading"
    >
      <v-card outlined class="mb-3">
        <v-card-text class="pb-2">
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-select
                v-model="fields.http_verb"
                :items="httpVerbs"
                label="Método"
                dense
                outlined
                :rules="[v => !!v || 'Obrigatório']"
              />
            </v-col>
            <v-col cols="12" sm="9">
              <v-text-field
                v-model="fields.url"
                label="URL"
                placeholder="https://exemplo.com/webhook"
                dense
                outlined
                :rules="[v => !!v || 'Obrigatório', urlRule]"
                @focus="focusedInput = 'url'"
              />
            </v-col>
          </v-row>

          <div v-if="fieldList && fieldList.length" class="variables-bar">
            <span class="caption grey--text mr-2">Variáveis:</span>
            <v-chip
              v-for="(variable, i) in fieldList"
              :key="i"
              x-small
              outlined
              class="ma-1 variable-chip"
              :disabled="!focusedInput"
              @click="insertVariable(variable)"
            >
              {{ variableLabel(variable) }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <v-tabs v-model="activeTab" grow show-arrows class="mb-2">
        <v-tab>Autenticação</v-tab>
        <v-tab>
          Headers
          <v-chip x-small class="ml-2" v-if="fields.headers.length">{{ fields.headers.length }}</v-chip>
        </v-tab>
        <v-tab>
          Query Params
          <v-chip x-small class="ml-2" v-if="fields.query_params.length">{{ fields.query_params.length }}</v-chip>
        </v-tab>
        <v-tab>Body</v-tab>
        <v-tab>Opções</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card outlined>
            <v-card-text>
              <v-radio-group
                v-model="fields.auth.type"
                row
                class="mt-0"
                hide-details
              >
                <v-radio label="Nenhuma" value="none" />
                <v-radio label="Basic" value="basic" />
                <v-radio label="Bearer Token" value="bearer" />
                <v-radio label="API Key" value="api_key" />
              </v-radio-group>

              <v-row v-if="fields.auth.type === 'basic'" dense class="mt-3">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="fields.auth.user"
                    label="Usuário"
                    dense
                    outlined
                    @focus="focusedInput = 'auth.user'"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="fields.auth.pass"
                    label="Senha"
                    :type="showPass ? 'text' : 'password'"
                    :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                    dense
                    outlined
                    @click:append="showPass = !showPass"
                    @focus="focusedInput = 'auth.pass'"
                  />
                </v-col>
              </v-row>

              <v-row v-if="fields.auth.type === 'bearer'" dense class="mt-3">
                <v-col cols="12">
                  <v-text-field
                    v-model="fields.auth.token"
                    label="Token"
                    :type="showPass ? 'text' : 'password'"
                    :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                    dense
                    outlined
                    @click:append="showPass = !showPass"
                    @focus="focusedInput = 'auth.token'"
                  />
                </v-col>
              </v-row>

              <v-row v-if="fields.auth.type === 'api_key'" dense class="mt-3">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="fields.auth.api_key_name"
                    label="Nome do parâmetro"
                    placeholder="X-API-Key"
                    dense
                    outlined
                    @focus="focusedInput = 'auth.api_key_name'"
                  />
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="fields.auth.api_key_value"
                    label="Valor"
                    :type="showPass ? 'text' : 'password'"
                    :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                    dense
                    outlined
                    @click:append="showPass = !showPass"
                    @focus="focusedInput = 'auth.api_key_value'"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="fields.auth.api_key_in"
                    :items="apiKeyLocations"
                    label="Enviar em"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined>
            <v-card-text>
              <div
                v-for="(item, i) in fields.headers"
                :key="`header-${i}`"
                class="kv-row"
              >
                <v-text-field
                  v-model="item.key"
                  label="Header"
                  placeholder="Content-Type"
                  dense
                  outlined
                  hide-details
                  class="kv-key"
                  @focus="focusedInput = `headers.${i}.key`"
                />
                <v-text-field
                  v-model="item.value"
                  label="Valor"
                  dense
                  outlined
                  hide-details
                  class="kv-value"
                  @focus="focusedInput = `headers.${i}.value`"
                />
                <v-btn icon small color="error" @click="removeRow('headers', i)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-btn
                small
                text
                color="primary"
                class="mt-2"
                @click="addRow('headers')"
              >
                <v-icon left small>mdi-plus</v-icon>
                Adicionar header
              </v-btn>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined>
            <v-card-text>
              <div
                v-for="(item, i) in fields.query_params"
                :key="`qp-${i}`"
                class="kv-row"
              >
                <v-text-field
                  v-model="item.key"
                  label="Parâmetro"
                  dense
                  outlined
                  hide-details
                  class="kv-key"
                  @focus="focusedInput = `query_params.${i}.key`"
                />
                <v-text-field
                  v-model="item.value"
                  label="Valor"
                  dense
                  outlined
                  hide-details
                  class="kv-value"
                  @focus="focusedInput = `query_params.${i}.value`"
                />
                <v-btn icon small color="error" @click="removeRow('query_params', i)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-btn
                small
                text
                color="primary"
                class="mt-2"
                @click="addRow('query_params')"
              >
                <v-icon left small>mdi-plus</v-icon>
                Adicionar parâmetro
              </v-btn>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="fields.body.mode"
                    :items="bodyModes"
                    label="Modo do corpo"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="6" v-if="fields.body.mode === 'raw'">
                  <v-select
                    v-model="fields.body.encoding"
                    :items="encodings"
                    label="Encoding"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
              </v-row>

              <div v-if="fields.body.mode === 'raw'" class="mt-3">
                <v-textarea
                  v-model="fields.body.raw"
                  :placeholder="rawPlaceholder"
                  :rules="[bodyRule]"
                  outlined
                  dense
                  rows="8"
                  spellcheck="false"
                  class="mono"
                  @focus="focusedInput = 'body.raw'"
                />
                <div v-if="jsonError" class="caption error--text">
                  JSON inválido: {{ jsonError }}
                </div>
                <v-btn
                  v-if="fields.body.encoding === 'json' && fields.body.raw"
                  x-small
                  text
                  @click="formatJson"
                >
                  <v-icon left x-small>mdi-code-json</v-icon>
                  Formatar JSON
                </v-btn>
              </div>

              <div v-if="fields.body.mode === 'form'" class="mt-3">
                <div
                  v-for="(item, i) in fields.body.form"
                  :key="`form-${i}`"
                  class="kv-row"
                >
                  <v-text-field
                    v-model="item.key"
                    label="Campo"
                    dense
                    outlined
                    hide-details
                    class="kv-key"
                    @focus="focusedInput = `body.form.${i}.key`"
                  />
                  <v-text-field
                    v-model="item.value"
                    label="Valor"
                    dense
                    outlined
                    hide-details
                    class="kv-value"
                    @focus="focusedInput = `body.form.${i}.value`"
                  />
                  <v-btn icon small color="error" @click="removeRow('body.form', i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <v-btn
                  small
                  text
                  color="primary"
                  class="mt-2"
                  @click="addRow('body.form')"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar campo
                </v-btn>
              </div>

              <div v-if="fields.body.mode === 'template' && fieldList.length" class="mt-3">
                <p class="caption mb-2">
                  Selecione os campos que farão parte do payload enviado:
                </p>
                <v-chip-group
                  v-model="fields.body.template_fields"
                  multiple
                  column
                >
                  <v-chip
                    v-for="(variable, i) in fieldList"
                    :key="i"
                    :value="variableValue(variable)"
                    filter
                    outlined
                    small
                  >
                    {{ variableLabel(variable) }}
                  </v-chip>
                </v-chip-group>
              </div>

              <div v-if="fields.body.mode === 'none'" class="mt-3 caption grey--text">
                Nenhum corpo será enviado.
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="fields.options.timeout"
                    label="Timeout (segundos)"
                    type="number"
                    min="1"
                    max="300"
                    dense
                    outlined
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="fields.options.retry_attempts"
                    label="Tentativas em caso de falha"
                    type="number"
                    min="0"
                    max="10"
                    dense
                    outlined
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="fields.options.retry_delay"
                    label="Intervalo entre tentativas (s)"
                    type="number"
                    min="0"
                    max="600"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-switch
                    v-model="fields.options.verify_ssl"
                    label="Verificar certificado SSL"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch
                    v-model="fields.options.follow_redirects"
                    label="Seguir redirecionamentos"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <div class="d-flex mt-3">
        <v-spacer />
        <v-btn small text @click="previewDialog = true">
          <v-icon left small>mdi-eye-outline</v-icon>
          Pré-visualizar
        </v-btn>
      </div>
    </v-form>

    <v-dialog v-model="previewDialog" max-width="720">
      <v-card>
        <v-card-title class="text-subtitle-1">
          Pré-visualização da requisição
        </v-card-title>
        <v-card-text>
          <pre class="mono preview-block">{{ previewText }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="previewDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";

const defaultFields = () => ({
  url: "",
  http_verb: "post",
  auth: {
    type: "none",
    user: "",
    pass: "",
    token: "",
    api_key_name: "",
    api_key_value: "",
    api_key_in: "header"
  },
  headers: [],
  query_params: [],
  body: {
    mode: "none",
    encoding: "json",
    raw: "",
    form: [],
    template_fields: []
  },
  options: {
    timeout: 30,
    retry_attempts: 0,
    retry_delay: 5,
    verify_ssl: true,
    follow_redirects: true
  }
});

export default {
  name: "sendWebhook",

  props: ["index", "index_actions", "actionTypeSelected", "rules_list"],

  data() {
    return {
      valid: true,
      loading: true,
      activeTab: 0,
      showPass: false,
      previewDialog: false,
      focusedInput: null,
      fieldList: [],
      fields: defaultFields(),
      httpVerbs: [
        { text: "GET", value: "get" },
        { text: "POST", value: "post" },
        { text: "PUT", value: "put" },
        { text: "PATCH", value: "patch" },
        { text: "DELETE", value: "delete" }
      ],
      bodyModes: [
        { text: "Nenhum", value: "none" },
        { text: "Raw (JSON / XML / Texto)", value: "raw" },
        { text: "Form (x-www-form-urlencoded)", value: "form" },
        // { text: "Template de campos", value: "template" }
      ],
      encodings: [
        { text: "JSON", value: "json" },
        { text: "XML", value: "xml" },
        { text: "Texto", value: "text" },
        { text: "x-www-form-urlencoded", value: "x-www-form-urlencoded" }
      ],
      apiKeyLocations: [
        { text: "Header", value: "header" },
        { text: "Query", value: "query" }
      ]
    };
  },

  computed: {
    ...mapState("rules", ["rules", "action_type_list"]),

    rawPlaceholder() {
      if (this.fields.body.encoding === "json") {
        return '{\n  "key": "value"\n}';
      }
      if (this.fields.body.encoding === "xml") {
        return "<root>\n  <key>value</key>\n</root>";
      }
      return "";
    },

    jsonError() {
      if (
        this.fields.body.mode !== "raw" ||
        this.fields.body.encoding !== "json" ||
        !this.fields.body.raw
      ) {
        return null;
      }
      try {
        JSON.parse(this.fields.body.raw);
        return null;
      } catch (e) {
        return "Verifique vírgulas, aspas e chaves do JSON";
      }
    },

    previewText() {
      const url = this.buildFullUrl();
      const lines = [`${(this.fields.http_verb || "GET").toUpperCase()} ${url}`];
      const headers = this.buildHeaders();
      Object.entries(headers).forEach(([k, v]) => lines.push(`${k}: ${v}`));
      const body = this.buildBodyString();
      if (body) {
        lines.push("");
        lines.push(body);
      }
      return lines.join("\n");
    }
  },

  watch: {
    fields: {
      deep: true,
      handler() {
        this.persist();
      }
    }
  },

  mounted() {
    const action = this.rules_list[this.index].actions[this.index_actions];
    const stored = action.fields || {};

    if (stored && stored.url && typeof stored.url === "object") {
      this.fields = this.migrateLegacy(stored);
    } else if (stored && Object.keys(stored).length) {
      this.fields = this.mergeWithDefaults(stored);
    }

    const webhookType = (this.action_type_list.data || []).find(
      e => e.display_name === "Webhook"
    );
    if (webhookType && webhookType.fields) {
      this.fieldList = webhookType.fields;
    }

    this.persist();
    this.loading = false;
  },

  methods: {
    ...mapMutations({ setRules: "rules/setRules" }),

    urlRule(value) {
      if (!value) return true;
      const hasVariable = /\{\{.*\}\}/.test(value);
      if (hasVariable) return true;
      try {
        new URL(value);
        return true;
      } catch {
        return "URL inválida";
      }
    },

    bodyRule() {
      return this.jsonError ? "JSON inválido" : true;
    },

    addRow(path) {
      const list = this.resolvePath(path);
      list.push({ key: "", value: "" });
    },

    removeRow(path, i) {
      const list = this.resolvePath(path);
      list.splice(i, 1);
    },

    resolvePath(path) {
      return path.split(".").reduce((acc, key) => acc[key], this.fields);
    },

    variableLabel(variable) {
      if (typeof variable === "string") return variable;
      return variable.display_name || variable.label || variable.name || variable.key || "";
    },

    variableValue(variable) {
      if (typeof variable === "string") return variable;
      return variable.name || variable.key || variable.value || this.variableLabel(variable);
    },

    insertVariable(variable) {
      if (!this.focusedInput) return;
      const token = `{{${this.variableValue(variable)}}}`;
      const path = this.focusedInput.split(".");
      const last = path.pop();
      const target = path.reduce((acc, key) => acc[key], this.fields);
      target[last] = (target[last] || "") + token;
    },

    formatJson() {
      try {
        this.fields.body.raw = JSON.stringify(
          JSON.parse(this.fields.body.raw),
          null,
          2
        );
      } catch {
        // mantém valor original quando inválido
      }
    },

    buildFullUrl() {
      const url = this.fields.url || "";
      const params = [...this.fields.query_params];
      if (
        this.fields.auth.type === "api_key" &&
        this.fields.auth.api_key_in === "query" &&
        this.fields.auth.api_key_name
      ) {
        params.push({
          key: this.fields.auth.api_key_name,
          value: this.fields.auth.api_key_value
        });
      }
      const qs = params
        .filter(p => p.key)
        .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || "")}`)
        .join("&");
      if (!qs) return url;
      return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
    },

    buildHeaders() {
      const headers = {};
      this.fields.headers.forEach(h => {
        if (h.key) headers[h.key] = h.value;
      });
      if (this.fields.body.mode === "raw") {
        const map = {
          json: "application/json",
          xml: "application/xml",
          text: "text/plain",
          "x-www-form-urlencoded": "application/x-www-form-urlencoded"
        };
        headers["Content-Type"] = headers["Content-Type"] || map[this.fields.body.encoding];
      }
      if (this.fields.body.mode === "form") {
        headers["Content-Type"] = headers["Content-Type"] || "application/x-www-form-urlencoded";
      }
      if (this.fields.body.mode === "template") {
        headers["Content-Type"] = headers["Content-Type"] || "application/json";
      }
      if (this.fields.auth.type === "basic" && (this.fields.auth.user || this.fields.auth.pass)) {
        const token = btoa(`${this.fields.auth.user}:${this.fields.auth.pass}`);
        headers["Authorization"] = `Basic ${token}`;
      }
      if (this.fields.auth.type === "bearer" && this.fields.auth.token) {
        headers["Authorization"] = `Bearer ${this.fields.auth.token}`;
      }
      if (
        this.fields.auth.type === "api_key" &&
        this.fields.auth.api_key_in === "header" &&
        this.fields.auth.api_key_name
      ) {
        headers[this.fields.auth.api_key_name] = this.fields.auth.api_key_value;
      }
      return headers;
    },

    buildBodyString() {
      const { mode, raw, form, template_fields } = this.fields.body;
      if (mode === "raw") return raw || "";
      if (mode === "form") {
        return form
          .filter(f => f.key)
          .map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value || "")}`)
          .join("&");
      }
      if (mode === "template") {
        const obj = {};
        (template_fields || []).forEach(f => {
          obj[f] = `{{${f}}}`;
        });
        return JSON.stringify(obj, null, 2);
      }
      return "";
    },

    persist() {
      const action = this.rules_list[this.index].actions[this.index_actions];
      action.fields = JSON.parse(JSON.stringify(this.fields));
      this.setRules(this.rules_list);
    },

    mergeWithDefaults(stored) {
      const base = defaultFields();
      return {
        ...base,
        ...stored,
        auth: { ...base.auth, ...(stored.auth || {}) },
        body: { ...base.body, ...(stored.body || {}) },
        options: { ...base.options, ...(stored.options || {}) },
        headers: Array.isArray(stored.headers) ? stored.headers : [],
        query_params: Array.isArray(stored.query_params) ? stored.query_params : []
      };
    },

    migrateLegacy(stored) {
      const next = defaultFields();
      const pick = (obj, key) => (obj && obj[key] && obj[key][key] !== undefined ? obj[key][key] : "");

      next.url = pick(stored, "url");
      const verb = pick(stored, "http_verb");
      next.http_verb = typeof verb === "object" && verb ? verb.value : verb || "post";

      const isAuth = pick(stored, "is_autenticated");
      if (isAuth) {
        const authType = pick(stored, "auth_type");
        const isApiKey =
          Array.isArray(authType) && authType.includes("api_key");
        if (isApiKey) {
          next.auth.type = "api_key";
          next.auth.api_key_name = "X-API-Key";
          next.auth.api_key_value = pick(stored, "api_key");
        } else {
          next.auth.type = "basic";
          next.auth.user = pick(stored, "user");
          next.auth.pass = pick(stored, "pass");
        }
      }

      const legacyHeader = pick(stored, "header");
      if (legacyHeader) {
        legacyHeader.split(/\r?\n/).forEach(line => {
          const idx = line.indexOf(":");
          if (idx > 0) {
            next.headers.push({
              key: line.substring(0, idx).trim(),
              value: line.substring(idx + 1).trim()
            });
          }
        });
      }

      const encoding = pick(stored, "encoding");
      next.body.encoding = Array.isArray(encoding) ? encoding[0] || "json" : encoding || "json";

      const hasCustomContent = pick(stored, "content");
      const customBody = pick(stored, "custom");
      const simpleContent = pick(stored, "simpleContent");

      if (hasCustomContent && customBody) {
        next.body.mode = "raw";
        next.body.raw = customBody;
      } else if (Array.isArray(simpleContent) && simpleContent.length) {
        next.body.mode = "template";
        next.body.template_fields = simpleContent;
      } else {
        next.body.mode = "none";
      }

      return next;
    }
  }
};
</script>

<style scoped>
.kv-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.kv-key {
  flex: 1;
}
.kv-value {
  flex: 2;
}
.variables-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: -4px;
}
.variable-chip {
  cursor: pointer;
}
.mono >>> textarea,
.mono {
  font-family: "Fira Code", "JetBrains Mono", monospace;
  font-size: 12px;
}
.preview-block {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 480px;
  overflow: auto;
}
</style>
